<template>
    <kt-modal
        :visible="visible"
        title="Export Data"
        size="modal-lg"
        @dismiss="$emit('dismiss')"
    >
        <template #body>
            <div class="container-fluid">
                <!-- Active Filters Display -->
                <div v-if="activeFilters.length > 0" class="mb-4">
                    <h6 class="fw-bold">Active Filters:</h6>
                    <div class="border rounded p-3 bg-light">
                        <div v-for="filter in activeFilters" :key="filter.label" class="mb-2">
                            <strong>{{ filter.label }}:</strong> {{ filter.value }}
                        </div>
                    </div>
                </div>

                <!-- Column Selection -->
                <div class="mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="fw-bold mb-0">Select Columns to Export:</h6>
                        <div>
                            <button 
                                @click="selectAllColumns" 
                                class="btn btn-sm btn-outline-primary me-2"
                            >
                                Select All
                            </button>
                            <button 
                                @click="deselectAllColumns" 
                                class="btn btn-sm btn-outline-secondary"
                            >
                                Deselect All
                            </button>
                        </div>
                    </div>
                    
                    <div class="border rounded p-3" style="max-height: 300px; overflow-y: auto;">
                        <div class="row">
                            <div 
                                v-for="column in exportColumns" 
                                :key="column.name"
                                class="col-md-6 mb-2"
                            >
                                <div class="form-check">
                                    <input 
                                        type="checkbox"
                                        class="form-check-input"
                                        :id="`col-${column.name}`"
                                        v-model="column.selected"
                                    />
                                    <label 
                                        class="form-check-label" 
                                        :for="`col-${column.name}`"
                                    >
                                        {{ column.label }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Export Format Selection -->
                <div class="mb-3">
                    <h6 class="fw-bold">Export Format:</h6>
                    <div class="btn-group w-100" role="group">
                        <input 
                            type="radio" 
                            class="btn-check" 
                            id="format-csv" 
                            v-model="exportFormat" 
                            value="csv"
                        />
                        <label class="btn btn-outline-primary" for="format-csv">
                            <i class="fas fa-file-csv me-2"></i>CSV
                        </label>

                        <input 
                            type="radio" 
                            class="btn-check" 
                            id="format-pdf" 
                            v-model="exportFormat" 
                            value="pdf"
                        />
                        <label class="btn btn-outline-primary" for="format-pdf">
                            <i class="fas fa-file-pdf me-2"></i>PDF
                        </label>
                    </div>
                </div>

                <!-- Record Count Info -->
                <div class="alert alert-info mb-0">
                    <i class="fas fa-info-circle me-2"></i>
                    Exporting <strong>{{ totalRecords }}</strong> record(s) with 
                    <strong>{{ selectedColumnCount }}</strong> column(s)
                </div>
            </div>
        </template>

        <template #footer>
            <div class="row w-100">
                <div class="col-6">
                    <button 
                        @click="$emit('dismiss')" 
                        class="btn btn-secondary form-control"
                    >
                        Cancel
                    </button>
                </div>
                <div class="col-6">
                    <button 
                        @click="handleExport" 
                        class="btn btn-primary form-control"
                        :disabled="selectedColumnCount === 0"
                    >
                        <i class="fas fa-download me-2"></i>
                        Export {{ exportFormat.toUpperCase() }}
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import ExportService, { type ExportColumn, type ExportFilter } from '@/services/ExportService';
import PdfExportService from '@/services/PdfExportService';

interface Props {
    visible: boolean;
    data: any[];
    columns: ExportColumn[];
    filters?: Record<string, any>;
    title?: string;
    filename?: string;
}

const props = withDefaults(defineProps<Props>(), {
    filters: () => ({}),
    title: 'Data Export',
    filename: 'export'
});

const emit = defineEmits<{
    dismiss: [];
    exported: [format: string];
}>();

const exportColumns = ref<ExportColumn[]>([]);
const exportFormat = ref<'csv' | 'pdf'>('csv');

const activeFilters = computed<ExportFilter[]>(() => {
    return ExportService.formatFiltersForDisplay(props.filters || {});
});

const selectedColumnCount = computed(() => {
    return exportColumns.value.filter(col => col.selected).length;
});

const totalRecords = computed(() => props.data.length);

watch(() => props.columns, (newColumns) => {
    exportColumns.value = newColumns.map(col => ({ ...col }));
}, { immediate: true });

const selectAllColumns = () => {
    exportColumns.value.forEach(col => col.selected = true);
};

const deselectAllColumns = () => {
    exportColumns.value.forEach(col => col.selected = false);
};

const handleExport = () => {
    try {
        const filename = `${props.filename}.${exportFormat.value}`;
        
        if (exportFormat.value === 'csv') {
            ExportService.exportToCSV(props.data, exportColumns.value, filename);
        } else if (exportFormat.value === 'pdf') {
            PdfExportService.exportToPDF(
                props.data, 
                exportColumns.value, 
                filename,
                props.title,
                props.filters
            );
        }

        emit('exported', exportFormat.value);
        emit('dismiss');
    } catch (error) {
        console.error('Export failed:', error);
        alert('Export failed. Please try again.');
    }
};
</script>

<style scoped>
.form-check-input:checked {
    background-color: #2980b9;
    border-color: #2980b9;
}

.btn-check:checked + .btn-outline-primary {
    background-color: #2980b9;
    border-color: #2980b9;
    color: white;
}
</style>
